import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  Activity, Thermometer, Gauge, Power, AlertTriangle, CheckCircle2,
  Plus, RefreshCw, Play, Square, RotateCcw, Wifi, WifiOff, Trash2, Wrench,
  Brain, Search, Send, Zap, TrendingUp, ShieldAlert, MessageSquare, Scan, X,
  Clock, Target, Layers, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const STATUS_COLORS = { online: 'bg-emerald-500', offline: 'bg-gray-400' };
const STATE_COLORS = { running: 'text-emerald-600', stopped: 'text-red-600' };
const SEV_COLORS = { critical: 'bg-red-600', high: 'bg-orange-500', medium: 'bg-yellow-500' };
const SEV_BG = { critical: 'bg-red-50 border-red-200', high: 'bg-orange-50 border-orange-200', medium: 'bg-yellow-50 border-yellow-200' };

const TABS = ['Dashboard', 'AI Anomaly Detection', 'Predictive Maintenance', 'Alert Correlation', 'Digital Twin', 'Detection Rules', 'Protocol Sim', 'Auto-Scan', 'Ask Your Plant'];

export default function ScadaDashboard() {
  const { user } = useAuth();
  const [sidebarTab, setSidebarTab] = useState('Dashboard');
  const [tab, setTab] = useState('Dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'sensor', location: '' });
  const [maintWindows, setMaintWindows] = useState([]);
  const [showMaint, setShowMaint] = useState(false);
  const [maintForm, setMaintForm] = useState({ device_id: '', device_name: '', start_time: '', end_time: '', reason: '' });

  // AI Anomaly Detection state
  const [anomalyStats, setAnomalyStats] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanHours, setScanHours] = useState(72);

  // NL Query state
  const [nlQuestion, setNlQuestion] = useState('');
  const [nlLoading, setNlLoading] = useState(false);
  const [nlConversation, setNlConversation] = useState([]);
  const [queryHistory, setQueryHistory] = useState([]);

  // Predictive Maintenance state
  const [predictions, setPredictions] = useState([]);
  const [predicting, setPredicting] = useState(false);
  const [predResult, setPredResult] = useState(null);
  const [predHours, setPredHours] = useState(72);

  // Alert Correlation state
  const [correlations, setCorrelations] = useState([]);
  const [correlating, setCorrelating] = useState(false);
  const [corrResult, setCorrResult] = useState(null);
  const [corrHours, setCorrHours] = useState(24);

  // Auto-Scan state
  const [scanConfig, setScanConfig] = useState(null);
  const [scanHistoryData, setScanHistoryData] = useState([]);
  const [triggeringScan, setTriggeringScan] = useState(false);

  // Digital Twin state
  const [twinStatus, setTwinStatus] = useState([]);
  const [buildingTwin, setBuildingTwin] = useState(false);

  // Detection Rules state
  const [detRules, setDetRules] = useState([]);
  const [ruleDesc, setRuleDesc] = useState('');
  const [generatingRule, setGeneratingRule] = useState(false);

  // Protocol Sim state
  const [protoResult, setProtoResult] = useState(null);
  const [simProtocol, setSimProtocol] = useState('modbus');
  const [simulating, setSimulating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, alertRes] = await Promise.all([
        api.get('/api/scada/dashboard'),
        api.get('/api/scada/alerts?limit=20'),
      ]);
      setDashboard(dashRes.data);
      setAlerts(alertRes.data.alerts || []);
      try { const { data: mw } = await api.get('/api/enhance/scada/maintenance'); setMaintWindows(mw.windows || []); } catch (e) { /* handled */ }
      if (dashRes.data.total_devices === 0) {
        await api.post('/api/scada/seed-demo');
        const { data } = await api.get('/api/scada/dashboard');
        setDashboard(data);
      }
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []);

  const fetchAnomalyData = useCallback(async () => {
    try {
      const [statsRes, anomRes] = await Promise.all([
        api.get('/api/scada/ai/anomaly-stats'),
        api.get('/api/scada/ai/anomalies?limit=30'),
      ]);
      setAnomalyStats(statsRes.data);
      setAnomalies(anomRes.data.anomalies || []);
    } catch (e) { /* handled */ }
  }, []);

  const fetchQueryHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/api/scada/ai/query-history?limit=10');
      setQueryHistory(data.queries || []);
    } catch (e) { /* handled */ }
  }, []);

  const fetchPredictions = useCallback(async () => {
    try {
      const { data } = await api.get('/api/scada/ai/predictions?limit=20');
      setPredictions(data.predictions || []);
    } catch (e) { /* handled */ }
  }, []);

  const fetchCorrelations = useCallback(async () => {
    try {
      const { data } = await api.get('/api/scada/ai/correlations?limit=10');
      setCorrelations(data.correlations || []);
    } catch (e) { /* handled */ }
  }, []);

  const fetchScanConfig = useCallback(async () => {
    try {
      const [cfgRes, histRes] = await Promise.all([
        api.get('/api/scada/ai/scan-config'),
        api.get('/api/scada/ai/scan-history?limit=20'),
      ]);
      setScanConfig(cfgRes.data);
      setScanHistoryData(histRes.data.scans || []);
    } catch (e) { /* handled */ }
  }, []);

  const fetchTwinStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/api/scada/ai/digital-twin/status');
      setTwinStatus(data.twins || []);
    } catch (e) { /* handled */ }
  }, []);

  const fetchDetRules = useCallback(async () => {
    try {
      const { data } = await api.get('/api/scada/ai/detection-rules');
      setDetRules(data.rules || []);
    } catch (e) { /* handled */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (tab === 'AI Anomaly Detection') fetchAnomalyData(); }, [tab, fetchAnomalyData]);
  useEffect(() => { if (tab === 'Ask Your Plant') fetchQueryHistory(); }, [tab, fetchQueryHistory]);
  useEffect(() => { if (tab === 'Predictive Maintenance') fetchPredictions(); }, [tab, fetchPredictions]);
  useEffect(() => { if (tab === 'Alert Correlation') fetchCorrelations(); }, [tab, fetchCorrelations]);
  useEffect(() => { if (tab === 'Auto-Scan') fetchScanConfig(); }, [tab, fetchScanConfig]);
  useEffect(() => { if (tab === 'Digital Twin') fetchTwinStatus(); }, [tab, fetchTwinStatus]);
  useEffect(() => { if (tab === 'Detection Rules') fetchDetRules(); }, [tab, fetchDetRules]);

  const handleControl = async (deviceId, action) => {
    try {
      await api.put(`/api/scada/devices/${deviceId}/control`, { action });
      toast.success(`Device ${action}ed`);
      fetchData();
    } catch { toast.error('Control action failed'); }
  };

  const handleViewHistory = async (device) => {
    setSelectedDevice(device);
    try {
      const { data } = await api.get(`/api/scada/devices/${device.id}/history?hours=48`);
      setHistory(data.readings || []);
    } catch { toast.error('Failed to load history'); }
  };

  const handleAddDevice = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    try {
      await api.post('/api/scada/devices', form);
      setForm({ name: '', type: 'sensor', location: '' });
      setShowAdd(false);
      fetchData();
      toast.success('Device added');
    } catch { toast.error('Failed to add device'); }
  };

  const handleDeleteDevice = async (id) => {
    try {
      await api.delete(`/api/scada/devices/${id}`);
      fetchData();
      toast.success('Device removed');
    } catch { toast.error('Failed'); }
  };

  const handleAckAlert = async (id) => {
    try {
      await api.put(`/api/scada/alerts/${id}`, { status: 'acknowledged' });
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: 'acknowledged' } : a));
    } catch (e) { /* handled */ }
  };

  // AI Anomaly Detection handlers
  const runAnomalyScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const { data } = await api.post('/api/scada/ai/anomaly-scan', { hours: scanHours });
      setScanResult(data);
      fetchAnomalyData();
      if (data.anomalies_found > 0) {
        toast.success(`Scan complete: ${data.anomalies_found} anomalies detected across ${data.devices_scanned} devices`);
      } else {
        toast.success(`Scan complete: No anomalies detected across ${data.devices_scanned} devices`);
      }
    } catch { toast.error('Anomaly scan failed'); }
    setScanning(false);
  };

  const handleAnomalyAction = async (id, status) => {
    try {
      await api.put(`/api/scada/ai/anomalies/${id}`, { status });
      setAnomalies(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      toast.success(`Anomaly ${status}`);
    } catch { toast.error('Failed'); }
  };

  // NL Query handler
  const handleNlQuery = async () => {
    if (!nlQuestion.trim()) return;
    const q = nlQuestion.trim();
    setNlConversation(prev => [...prev, { role: 'user', content: q }]);
    setNlQuestion('');
    setNlLoading(true);
    try {
      const { data } = await api.post('/api/scada/ai/query', { question: q });
      setNlConversation(prev => [...prev, { role: 'assistant', content: data.answer, devices: data.devices_in_context }]);
    } catch {
      setNlConversation(prev => [...prev, { role: 'assistant', content: 'Sorry, I couldn\'t process that query. Please try again.' }]);
    }
    setNlLoading(false);
  };

  // Predictive Maintenance handler
  const runPrediction = async () => {
    setPredicting(true);
    setPredResult(null);
    try {
      const { data } = await api.post('/api/scada/ai/predict', { hours: predHours });
      setPredResult(data);
      fetchPredictions();
      toast.success(`Analysis complete: ${data.devices_analyzed} devices analyzed`);
    } catch { toast.error('Predictive analysis failed'); }
    setPredicting(false);
  };

  // Alert Correlation handler
  const runCorrelation = async () => {
    setCorrelating(true);
    setCorrResult(null);
    try {
      const { data } = await api.post('/api/scada/ai/correlate', { hours: corrHours });
      setCorrResult(data);
      fetchCorrelations();
      toast.success(`Correlation complete: ${data.total_events} events analyzed`);
    } catch { toast.error('Correlation analysis failed'); }
    setCorrelating(false);
  };

  // Auto-Scan handlers
  const toggleAutoScan = async (enabled) => {
    try {
      const { data } = await api.put('/api/scada/ai/scan-config', { ...scanConfig, enabled });
      setScanConfig(data);
      toast.success(enabled ? 'Auto-scan enabled' : 'Auto-scan disabled');
    } catch { toast.error('Failed to update config'); }
  };

  const triggerManualScan = async () => {
    setTriggeringScan(true);
    try {
      await api.post('/api/scada/ai/scan-now');
      toast.success('Scan triggered — results will appear shortly');
      setTimeout(fetchScanConfig, 8000);
    } catch { toast.error('Failed to trigger scan'); }
    setTriggeringScan(false);
  };

  const updateScanInterval = async (hours) => {
    try {
      const { data } = await api.put('/api/scada/ai/scan-config', { ...scanConfig, interval_hours: hours });
      setScanConfig(data);
      toast.success(`Scan interval set to ${hours}h`);
    } catch { toast.error('Failed'); }
  };

  // Digital Twin handlers
  const buildTwins = async () => {
    setBuildingTwin(true);
    try {
      const { data } = await api.post('/api/scada/ai/digital-twin/build', { hours: 72 });
      toast.success(`Built ${data.twins_built} digital twins`);
      fetchTwinStatus();
    } catch { toast.error('Failed to build twins'); }
    setBuildingTwin(false);
  };

  // Detection Rule handler
  const generateRule = async () => {
    if (!ruleDesc.trim()) return;
    setGeneratingRule(true);
    try {
      const { data } = await api.post('/api/scada/ai/generate-rule', { description: ruleDesc });
      toast.success(`Rule generated: ${data.name}`);
      setRuleDesc('');
      fetchDetRules();
    } catch { toast.error('Rule generation failed'); }
    setGeneratingRule(false);
  };

  // Protocol Sim handler
  const runProtocolSim = async () => {
    setSimulating(true);
    try {
      const deviceId = dashboard?.devices?.[0]?.id;
      const { data } = await api.post('/api/scada/ai/protocol/simulate', { protocol: simProtocol, device_id: deviceId, num_frames: 10 });
      setProtoResult(data);
      toast.success(`${data.frames_generated} ${simProtocol.toUpperCase()} frames simulated`);
    } catch { toast.error('Simulation failed'); }
    setSimulating(false);
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={sidebarTab} onTabChange={setSidebarTab} />
      <main className="flex-1 flex items-center justify-center"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="scada-dashboard">
      <Sidebar activeTab={sidebarTab} onTabChange={setSidebarTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">SCADA / IoT Monitor</h1>
              <p className="text-sm text-gray-500 mt-1">Real-time device monitoring, AI anomaly detection, and intelligent queries</p>
            </div>
            <div className="flex gap-2">
              {tab === 'Dashboard' && (
                <>
                  <Button variant="outline" size="sm" onClick={fetchData} className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
                  <Button onClick={() => setShowAdd(!showAdd)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs" data-testid="scada-add-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add Device
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="scada-tabs">
            {TABS.map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 rounded-md text-xs font-medium transition-all ${tab === t ? 'bg-white text-[#D4A843] shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
                data-testid={`scada-tab-${t.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {t === 'AI Anomaly Detection' && <Brain className="w-3.5 h-3.5 inline mr-1.5" />}
                {t === 'Predictive Maintenance' && <Clock className="w-3.5 h-3.5 inline mr-1.5" />}
                {t === 'Alert Correlation' && <Layers className="w-3.5 h-3.5 inline mr-1.5" />}
                {t === 'Digital Twin' && <Target className="w-3.5 h-3.5 inline mr-1.5" />}
                {t === 'Detection Rules' && <ShieldAlert className="w-3.5 h-3.5 inline mr-1.5" />}
                {t === 'Protocol Sim' && <Gauge className="w-3.5 h-3.5 inline mr-1.5" />}
                {t === 'Auto-Scan' && <Scan className="w-3.5 h-3.5 inline mr-1.5" />}
                {t === 'Ask Your Plant' && <MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />}
                {t === 'Dashboard' && <Activity className="w-3.5 h-3.5 inline mr-1.5" />}
                {t}
              </button>
            ))}
          </div>

          {/* ═══════════ DASHBOARD TAB ═══════════ */}
          {tab === 'Dashboard' && (
            <>
              {/* Stats */}
              {dashboard && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
                    <Activity className="w-5 h-5 text-[#D4A843] mb-2" />
                    <p className="font-heading text-2xl font-bold text-gray-900">{dashboard.total_devices}</p>
                    <p className="text-xs text-gray-500">Total Devices</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 hover-lift">
                    <Wifi className="w-5 h-5 text-emerald-600 mb-2" />
                    <p className="font-heading text-2xl font-bold text-emerald-700">{dashboard.online}</p>
                    <p className="text-xs text-emerald-600">Online</p>
                  </div>
                  <div className="bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg p-5 hover-lift">
                    <Power className="w-5 h-5 text-[#D4A843] mb-2" />
                    <p className="font-heading text-2xl font-bold text-[#D4A843]">{dashboard.running}</p>
                    <p className="text-xs text-[#D4A843]">Running</p>
                  </div>
                  <div className={`rounded-lg p-5 hover-lift ${dashboard.active_alerts > 0 ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'}`}>
                    <AlertTriangle className={`w-5 h-5 mb-2 ${dashboard.active_alerts > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                    <p className={`font-heading text-2xl font-bold ${dashboard.active_alerts > 0 ? 'text-red-700' : 'text-gray-900'}`}>{dashboard.active_alerts}</p>
                    <p className="text-xs text-gray-500">Active Alerts</p>
                  </div>
                </div>
              )}

              {/* Add Device Form */}
              {showAdd && (
                <div className="bg-white border border-gray-200 rounded-lg p-5 animate-fade-in" data-testid="scada-add-form">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div><Label className="text-xs">Device Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 rounded-lg" data-testid="scada-device-name" /></div>
                    <div><Label className="text-xs">Type</Label><Input value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="mt-1 rounded-lg" placeholder="sensor, hvac, generator..." data-testid="scada-device-type" /></div>
                    <div><Label className="text-xs">Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="mt-1 rounded-lg" data-testid="scada-device-location" /></div>
                  </div>
                  <Button onClick={handleAddDevice} className="mt-3 bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs" data-testid="scada-save-device">Save Device</Button>
                </div>
              )}

              {/* Device Detail */}
              {selectedDevice && (
                <div className="bg-white border border-[#D4A843]/20 rounded-lg p-6 animate-fade-in" data-testid="scada-device-detail">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{selectedDevice.name}</h3>
                      <p className="text-xs text-gray-500">{selectedDevice.location} | {selectedDevice.type}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedDevice(null); setHistory([]); }} className="text-xs rounded-lg" data-testid="scada-close-detail">Close</Button>
                  </div>
                  {selectedDevice.last_reading && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {Object.entries(selectedDevice.last_reading).map(([k, v]) => (
                        <div key={k} className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-[10px] text-gray-400 uppercase">{k.replace('_', ' ')}</p>
                          <p className="text-xl font-bold text-gray-900">{v}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {history.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2">Last 48h Readings ({history.length})</h4>
                      <div className="max-h-[200px] overflow-y-auto space-y-1">
                        {history.slice(0, 30).map(r => (
                          <div key={r.id} className="flex items-center gap-3 text-[10px] p-1.5 rounded bg-gray-50">
                            <span className="text-gray-400 font-mono w-32">{new Date(r.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            {Object.entries(r.values || {}).map(([k, v]) => (
                              <span key={k} className="text-gray-600"><span className="text-gray-400">{k}:</span> <span className="font-bold">{v}</span></span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Device Grid */}
              {!selectedDevice && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="scada-device-grid">
                  {(dashboard?.devices || []).map(device => (
                    <div key={device.id} className={`bg-white border rounded-lg p-5 ${device.control_state === 'running' ? 'border-emerald-200' : 'border-gray-200'}`} data-testid={`scada-device-${device.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${STATUS_COLORS[device.status] || 'bg-gray-400'} ${device.status === 'online' ? 'animate-pulse' : ''}`} />
                          <h4 className="text-sm font-bold text-gray-900">{device.name}</h4>
                          <span className={`text-[10px] font-bold uppercase ${STATE_COLORS[device.control_state] || 'text-gray-500'}`}>{device.control_state}</span>
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-500" onClick={() => handleDeleteDevice(device.id)} data-testid={`scada-del-${device.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <p className="text-[10px] text-gray-400 mb-3">{device.location} | {device.type}</p>
                      {device.last_reading && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {Object.entries(device.last_reading).map(([k, v]) => {
                            const thresh = device.thresholds?.[k];
                            const isHigh = thresh && v > thresh.max;
                            const isLow = thresh && v < thresh.min;
                            return (
                              <div key={k} className={`px-2.5 py-1.5 rounded text-center ${isHigh ? 'bg-red-100' : isLow ? 'bg-blue-100' : 'bg-gray-50'}`}>
                                <p className="text-[8px] text-gray-400 uppercase">{k.replace('_', ' ')}</p>
                                <p className={`text-sm font-bold ${isHigh ? 'text-red-600' : isLow ? 'text-blue-600' : 'text-gray-900'}`}>{v}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="text-xs h-7 rounded" onClick={() => handleControl(device.id, 'start')} data-testid={`scada-start-${device.id}`}>
                          <Play className="w-3 h-3 mr-0.5" /> Start
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 rounded text-red-600 border-red-200" onClick={() => handleControl(device.id, 'stop')} data-testid={`scada-stop-${device.id}`}>
                          <Square className="w-3 h-3 mr-0.5" /> Stop
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 rounded" onClick={() => handleControl(device.id, 'restart')} data-testid={`scada-restart-${device.id}`}>
                          <RotateCcw className="w-3 h-3 mr-0.5" /> Restart
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 rounded text-[#D4A843]" onClick={() => handleViewHistory(device)} data-testid={`scada-history-${device.id}`}>
                          History
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Alerts */}
              {alerts.length > 0 && !selectedDevice && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" /> Threshold Alerts
                  </h3>
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {alerts.map(a => (
                      <div key={a.id} className={`flex items-center justify-between p-3 rounded-lg ${a.status === 'active' ? 'bg-red-50' : 'bg-gray-50'}`} data-testid={`scada-alert-${a.id}`}>
                        <div>
                          <p className="text-xs font-medium text-gray-900">{a.device_name} — {a.metric} {a.alert_type === 'high' ? 'too high' : 'too low'}</p>
                          <p className="text-[10px] text-gray-400">Value: {a.value} | Threshold: {a.threshold} | {new Date(a.timestamp).toLocaleString()}</p>
                        </div>
                        {a.status === 'active' && (
                          <Button size="sm" variant="outline" className="text-xs h-6 rounded" onClick={() => handleAckAlert(a.id)} data-testid={`scada-ack-${a.id}`}>Acknowledge</Button>
                        )}
                        {a.status === 'acknowledged' && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Maintenance Windows */}
              {!selectedDevice && (
                <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="scada-maintenance-section">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-[#D4A843]" /> Maintenance Windows
                    </h3>
                    <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setShowMaint(!showMaint)} data-testid="scada-maint-toggle">
                      {showMaint ? 'Cancel' : 'Schedule'}
                    </Button>
                  </div>
                  {showMaint && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 animate-fade-in" data-testid="scada-maint-form">
                      <select value={maintForm.device_id} onChange={e => { const d = dashboard?.devices?.find(x => x.id === e.target.value); setMaintForm({...maintForm, device_id: e.target.value, device_name: d?.name || ''}); }} className="h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="scada-maint-device">
                        <option value="">Select device...</option>
                        {(dashboard?.devices || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                      <Input type="datetime-local" value={maintForm.start_time} onChange={e => setMaintForm({...maintForm, start_time: e.target.value})} className="rounded-lg text-xs" data-testid="scada-maint-start" />
                      <Input type="datetime-local" value={maintForm.end_time} onChange={e => setMaintForm({...maintForm, end_time: e.target.value})} className="rounded-lg text-xs" data-testid="scada-maint-end" />
                      <Input value={maintForm.reason} onChange={e => setMaintForm({...maintForm, reason: e.target.value})} placeholder="Reason..." className="rounded-lg text-xs" data-testid="scada-maint-reason" />
                      <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs" onClick={async () => {
                        if (!maintForm.device_id || !maintForm.start_time || !maintForm.end_time) { toast.error('Fill all fields'); return; }
                        try {
                          await api.post('/api/enhance/scada/maintenance', maintForm);
                          toast.success('Maintenance window scheduled');
                          setShowMaint(false);
                          setMaintForm({ device_id: '', device_name: '', start_time: '', end_time: '', reason: '' });
                          fetchData();
                        } catch { toast.error('Failed to schedule'); }
                      }} data-testid="scada-maint-save">Save</Button>
                    </div>
                  )}
                  {maintWindows.length > 0 ? (
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {maintWindows.map(w => (
                        <div key={w.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50" data-testid={`scada-maint-${w.id}`}>
                          <div>
                            <p className="text-xs font-medium text-gray-900">{w.device_name} — {w.reason}</p>
                            <p className="text-[10px] text-gray-400">{new Date(w.start_time).toLocaleString()} &rarr; {new Date(w.end_time).toLocaleString()}</p>
                          </div>
                          <Button size="sm" variant="ghost" className="text-xs h-6 text-red-500" onClick={async () => { await api.delete(`/api/enhance/scada/maintenance/${w.id}`); setMaintWindows(prev => prev.filter(x => x.id !== w.id)); toast.success('Removed'); }} data-testid={`scada-maint-del-${w.id}`}>Remove</Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No scheduled maintenance windows</p>
                  )}
                </div>
              )}
            </>
          )}

          {/* ═══════════ AI ANOMALY DETECTION TAB ═══════════ */}
          {tab === 'AI Anomaly Detection' && (
            <>
              {/* Anomaly Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
                  <Brain className="w-5 h-5 text-[#D4A843] mb-2" />
                  <p className="font-heading text-2xl font-bold text-gray-900">{anomalyStats?.total_anomalies || 0}</p>
                  <p className="text-xs text-gray-500">Total Detected</p>
                </div>
                <div className={`rounded-lg p-5 hover-lift ${(anomalyStats?.active || 0) > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-gray-200'}`}>
                  <ShieldAlert className={`w-5 h-5 mb-2 ${(anomalyStats?.active || 0) > 0 ? 'text-amber-600' : 'text-gray-400'}`} />
                  <p className={`font-heading text-2xl font-bold ${(anomalyStats?.active || 0) > 0 ? 'text-amber-700' : 'text-gray-900'}`}>{anomalyStats?.active || 0}</p>
                  <p className="text-xs text-gray-500">Active</p>
                </div>
                <div className={`rounded-lg p-5 hover-lift ${(anomalyStats?.critical || 0) > 0 ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'}`}>
                  <Zap className={`w-5 h-5 mb-2 ${(anomalyStats?.critical || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                  <p className={`font-heading text-2xl font-bold ${(anomalyStats?.critical || 0) > 0 ? 'text-red-700' : 'text-gray-900'}`}>{anomalyStats?.critical || 0}</p>
                  <p className="text-xs text-gray-500">Critical</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
                  <TrendingUp className="w-5 h-5 text-orange-500 mb-2" />
                  <p className="font-heading text-2xl font-bold text-gray-900">{anomalyStats?.high || 0}</p>
                  <p className="text-xs text-gray-500">High Severity</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 hover-lift">
                  <Scan className="w-5 h-5 text-emerald-500 mb-2" />
                  <p className="font-heading text-2xl font-bold text-gray-900">{anomalyStats?.last_scan ? anomalyStats.last_scan.devices_scanned : '—'}</p>
                  <p className="text-xs text-gray-500">Last Scan Devices</p>
                </div>
              </div>

              {/* Scan Controls */}
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="anomaly-scan-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <Brain className="w-4 h-4 text-[#D4A843]" /> Isolation Forest Anomaly Scanner
                    </h3>
                    <p className="text-[10px] text-gray-400 mt-1">
                      Machine learning model analyzes device readings to detect patterns that deviate from normal behavior.
                      {anomalyStats?.last_scan && ` Last scan: ${new Date(anomalyStats.last_scan.timestamp).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-[10px] text-gray-400 whitespace-nowrap">Analysis Window</Label>
                      <select
                        value={scanHours}
                        onChange={e => setScanHours(Number(e.target.value))}
                        className="h-8 rounded-lg border border-gray-200 px-2 text-xs bg-white"
                        data-testid="anomaly-scan-hours"
                      >
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                        <option value={72}>72 hours</option>
                        <option value={168}>7 days</option>
                      </select>
                    </div>
                    <Button
                      onClick={runAnomalyScan}
                      disabled={scanning}
                      className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs"
                      data-testid="anomaly-scan-btn"
                    >
                      {scanning ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Scan className="w-3.5 h-3.5 mr-1.5" />}
                      {scanning ? 'Scanning...' : 'Run Anomaly Scan'}
                    </Button>
                  </div>
                </div>

                {/* Scan Result Summary */}
                {scanResult && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg animate-fade-in" data-testid="anomaly-scan-result">
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-gray-500">Devices scanned: <span className="font-bold text-gray-900">{scanResult.devices_scanned}</span></span>
                      <span className="text-gray-500">Hours analyzed: <span className="font-bold text-gray-900">{scanResult.hours_analyzed}</span></span>
                      <span className={scanResult.anomalies_found > 0 ? 'text-red-600 font-bold' : 'text-emerald-600 font-bold'}>
                        {scanResult.anomalies_found > 0 ? `${scanResult.anomalies_found} anomalies found` : 'No anomalies detected'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Per-Device Breakdown */}
              {anomalyStats?.by_device && Object.keys(anomalyStats.by_device).length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Active Anomalies by Device</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(anomalyStats.by_device).map(([device, count]) => (
                      <div key={device} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <span className="text-xs font-medium text-gray-900">{device}</span>
                        <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Anomaly List */}
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="anomaly-list">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-red-500" /> Detected Anomalies
                </h3>
                {anomalies.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {anomalies.map(a => (
                      <div key={a.id} className={`flex items-start justify-between p-4 rounded-lg border ${a.status === 'active' ? SEV_BG[a.severity] || 'bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'}`} data-testid={`anomaly-${a.id}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-block w-2 h-2 rounded-full ${SEV_COLORS[a.severity] || 'bg-gray-400'}`} />
                            <span className="text-xs font-bold text-gray-900">{a.device_name}</span>
                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${a.severity === 'critical' ? 'bg-red-100 text-red-700' : a.severity === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {a.severity}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${a.status === 'active' ? 'bg-red-100 text-red-600' : a.status === 'investigating' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                              {a.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-1.5">
                            {Object.entries(a.values || {}).map(([k, v]) => (
                              <span key={k} className="text-[10px] text-gray-600 bg-white/70 px-2 py-0.5 rounded">
                                <span className="text-gray-400">{k}:</span> <span className="font-bold">{v}</span>
                              </span>
                            ))}
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1.5">
                            Score: {a.anomaly_score} | {new Date(a.timestamp).toLocaleString()}
                          </p>
                        </div>
                        {a.status === 'active' && (
                          <div className="flex gap-1.5 ml-3 shrink-0">
                            <Button size="sm" variant="outline" className="text-[10px] h-6 rounded px-2" onClick={() => handleAnomalyAction(a.id, 'investigating')} data-testid={`anomaly-investigate-${a.id}`}>Investigate</Button>
                            <Button size="sm" variant="outline" className="text-[10px] h-6 rounded px-2 text-emerald-600 border-emerald-200" onClick={() => handleAnomalyAction(a.id, 'dismissed')} data-testid={`anomaly-dismiss-${a.id}`}>Dismiss</Button>
                          </div>
                        )}
                        {a.status !== 'active' && (
                          <CheckCircle2 className="w-4 h-4 text-gray-400 ml-3 shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Brain className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No anomalies detected yet. Run a scan to analyze device readings.</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ═══════════ PREDICTIVE MAINTENANCE TAB ═══════════ */}
          {tab === 'Predictive Maintenance' && (
            <>
              {/* Scan Controls */}
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="predict-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#D4A843]" /> Predictive Maintenance Analyzer
                    </h3>
                    <p className="text-[10px] text-gray-400 mt-1">
                      Linear regression on device readings to forecast when metrics will breach thresholds.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-[10px] text-gray-400 whitespace-nowrap">Analysis Window</Label>
                      <select value={predHours} onChange={e => setPredHours(Number(e.target.value))} className="h-8 rounded-lg border border-gray-200 px-2 text-xs bg-white" data-testid="predict-hours">
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                        <option value={72}>72 hours</option>
                        <option value={168}>7 days</option>
                      </select>
                    </div>
                    <Button onClick={runPrediction} disabled={predicting} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs" data-testid="predict-btn">
                      {predicting ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5 mr-1.5" />}
                      {predicting ? 'Analyzing...' : 'Run Prediction'}
                    </Button>
                  </div>
                </div>

                {predResult && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg animate-fade-in" data-testid="predict-result">
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-gray-500">Devices analyzed: <span className="font-bold text-gray-900">{predResult.devices_analyzed}</span></span>
                      <span className="text-gray-500">Hours analyzed: <span className="font-bold text-gray-900">{predResult.hours_analyzed}</span></span>
                      {predResult.critical_count > 0 && <span className="text-red-600 font-bold">{predResult.critical_count} critical</span>}
                      {predResult.high_count > 0 && <span className="text-orange-600 font-bold">{predResult.high_count} high risk</span>}
                      {predResult.critical_count === 0 && predResult.high_count === 0 && <span className="text-emerald-600 font-bold">All devices within safe margins</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Prediction Cards */}
              <div className="space-y-4" data-testid="predict-list">
                {(predResult?.predictions || predictions).length > 0 ? (
                  (predResult?.predictions || predictions).map(pred => {
                    const riskColors = { critical: 'border-red-400 bg-red-50', high: 'border-orange-400 bg-orange-50', medium: 'border-yellow-400 bg-yellow-50', low: 'border-gray-200 bg-white', safe: 'border-emerald-200 bg-emerald-50' };
                    const riskText = { critical: 'text-red-700', high: 'text-orange-700', medium: 'text-yellow-700', low: 'text-gray-600', safe: 'text-emerald-700' };
                    const riskBadge = { critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-gray-100 text-gray-600', safe: 'bg-emerald-100 text-emerald-700' };
                    return (
                      <div key={pred.id} className={`border rounded-lg p-5 ${riskColors[pred.overall_risk] || 'border-gray-200 bg-white'}`} data-testid={`predict-${pred.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-bold text-gray-900">{pred.device_name}</h4>
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${riskBadge[pred.overall_risk] || ''}`}>{pred.overall_risk}</span>
                            <span className="text-[10px] text-gray-400">{pred.device_type} | {pred.location}</span>
                          </div>
                          <span className="text-[10px] text-gray-400">{pred.hours_analyzed}h analyzed</span>
                        </div>

                        {/* Metric Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                          {pred.metrics.map(m => (
                            <div key={m.metric} className={`bg-white/80 rounded-lg p-3 border ${m.risk !== 'safe' ? 'border-gray-300' : 'border-gray-100'}`}>
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[10px] text-gray-400 uppercase font-medium">{m.metric.replace('_', ' ')}</span>
                                <div className="flex items-center gap-1">
                                  {m.trend === 'rising' && <ArrowUpRight className="w-3 h-3 text-red-500" />}
                                  {m.trend === 'falling' && <ArrowDownRight className="w-3 h-3 text-blue-500" />}
                                  {m.trend === 'stable' && <Minus className="w-3 h-3 text-gray-400" />}
                                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${riskBadge[m.risk] || 'bg-gray-100 text-gray-500'}`}>{m.risk}</span>
                                </div>
                              </div>
                              <p className="text-lg font-bold text-gray-900">{m.current_value}</p>
                              <p className="text-[9px] text-gray-400">avg: {m.avg_value} | min: {m.min_value} | max: {m.max_value}</p>
                              <p className="text-[9px] text-gray-400">Range: {m.threshold_min} – {m.threshold_max} | Slope: {m.slope_per_hour}/h</p>
                              {m.hours_to_breach && (
                                <p className={`text-[10px] font-bold mt-1 ${riskText[m.risk] || 'text-gray-600'}`}>
                                  Breach in {m.hours_to_breach < 24 ? `${m.hours_to_breach}h` : `${Math.round(m.hours_to_breach / 24)}d`} ({m.breach_type} threshold)
                                </p>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Recommendations */}
                        {pred.recommendations?.length > 0 && (
                          <div className="space-y-1">
                            {pred.recommendations.map((r, i) => (
                              <p key={`item-${i}`} className={`text-[10px] ${pred.overall_risk === 'critical' ? 'text-red-600' : pred.overall_risk === 'high' ? 'text-orange-600' : 'text-gray-500'}`}>
                                {pred.overall_risk === 'critical' || pred.overall_risk === 'high' ? <AlertTriangle className="w-3 h-3 inline mr-1" /> : <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                                {r}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                    <TrendingUp className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No predictions yet. Run an analysis to forecast device health.</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ═══════════ ALERT CORRELATION TAB ═══════════ */}
          {tab === 'Alert Correlation' && (
            <>
              {/* Correlation Controls */}
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="correlate-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-[#D4A843]" /> Cross-Domain Alert Correlation
                    </h3>
                    <p className="text-[10px] text-gray-400 mt-1">
                      AI-powered analysis correlating SCADA alerts, Sysmon IOC flags, IT escalations, and ML anomalies to detect coordinated attack patterns.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-[10px] text-gray-400 whitespace-nowrap">Time Window</Label>
                      <select value={corrHours} onChange={e => setCorrHours(Number(e.target.value))} className="h-8 rounded-lg border border-gray-200 px-2 text-xs bg-white" data-testid="correlate-hours">
                        <option value={6}>6 hours</option>
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                        <option value={168}>7 days</option>
                        <option value={720}>30 days</option>
                      </select>
                    </div>
                    <Button onClick={runCorrelation} disabled={correlating} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs" data-testid="correlate-btn">
                      {correlating ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Target className="w-3.5 h-3.5 mr-1.5" />}
                      {correlating ? 'Correlating...' : 'Run Correlation'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Latest Correlation Result */}
              {(corrResult || correlations[0]) && (() => {
                const result = corrResult || correlations[0];
                const levelColors = { none: 'bg-gray-50 border-gray-200', low: 'bg-emerald-50 border-emerald-200', medium: 'bg-yellow-50 border-yellow-200', high: 'bg-orange-50 border-orange-200', critical: 'bg-red-50 border-red-200', unknown: 'bg-gray-50 border-gray-200' };
                const levelBadge = { none: 'bg-gray-100 text-gray-600', low: 'bg-emerald-100 text-emerald-700', medium: 'bg-yellow-100 text-yellow-700', high: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700', unknown: 'bg-gray-100 text-gray-600' };
                return (
                  <>
                    {/* Summary Card */}
                    <div className={`border rounded-lg p-6 ${levelColors[result.risk_level] || levelColors.unknown}`} data-testid="correlate-result">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${levelBadge[result.risk_level] || levelBadge.unknown}`}>
                            {result.risk_level} risk
                          </span>
                          <span className="text-[10px] text-gray-400">{result.total_events} events analyzed over {result.hours_analyzed}h</span>
                        </div>
                        <span className="text-[10px] text-gray-400">{new Date(result.timestamp).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-gray-800 leading-relaxed">{result.summary}</p>

                      {/* Domain Breakdown */}
                      <div className="flex flex-wrap gap-2 mt-4">
                        {Object.entries(result.domains || {}).map(([domain, count]) => (
                          <div key={domain} className="flex items-center gap-1.5 bg-white/80 border border-gray-200 rounded-lg px-3 py-1.5">
                            <span className="text-[10px] text-gray-500">{domain.replace('_', ' ')}</span>
                            <span className="text-[10px] font-bold text-gray-900">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Correlations List */}
                    {result.correlations?.length > 0 && (
                      <div className="space-y-3" data-testid="correlate-list">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                          <Target className="w-4 h-4 text-red-500" /> Identified Correlations ({result.correlations.length})
                        </h3>
                        {result.correlations.map((c, i) => {
                          const cBadge = { low: 'bg-emerald-100 text-emerald-700', medium: 'bg-yellow-100 text-yellow-700', high: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700' };
                          return (
                            <div key={`item-${i}`} className={`bg-white border rounded-lg p-5 ${c.severity === 'critical' ? 'border-red-300' : c.severity === 'high' ? 'border-orange-300' : 'border-gray-200'}`} data-testid={`correlation-${i}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${cBadge[c.severity] || 'bg-gray-100 text-gray-600'}`}>{c.severity}</span>
                                <h4 className="text-xs font-bold text-gray-900">{c.title}</h4>
                                <span className="text-[10px] text-gray-400">Confidence: {c.confidence}</span>
                              </div>
                              <p className="text-[11px] text-gray-700 leading-relaxed mb-2">{c.description}</p>

                              {c.domains_involved?.length > 0 && (
                                <div className="flex gap-1.5 mb-2">
                                  {c.domains_involved.map(d => (
                                    <span key={d} className="text-[9px] px-2 py-0.5 bg-[#D4A843]/5 text-[#D4A843] rounded font-medium">{d}</span>
                                  ))}
                                </div>
                              )}

                              {c.attack_vector && (
                                <p className="text-[10px] text-gray-500 mb-1"><span className="font-medium">Attack Vector:</span> {c.attack_vector}</p>
                              )}

                              {c.mitre_techniques?.length > 0 && (
                                <p className="text-[10px] text-gray-500 mb-2"><span className="font-medium">MITRE:</span> {c.mitre_techniques.join(', ')}</p>
                              )}

                              {c.evidence?.length > 0 && (
                                <div className="mb-2">
                                  <p className="text-[10px] font-medium text-gray-500 mb-1">Evidence:</p>
                                  <ul className="space-y-0.5">
                                    {c.evidence.map((e, j) => (
                                      <li key={`j-${j}`} className="text-[10px] text-gray-600 pl-3 border-l-2 border-gray-200">{e}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {c.recommended_actions?.length > 0 && (
                                <div>
                                  <p className="text-[10px] font-medium text-gray-500 mb-1">Recommended Actions:</p>
                                  {c.recommended_actions.map((a, j) => (
                                    <p key={`j-${j}`} className="text-[10px] text-[#D4A843] pl-3 border-l-2 border-[#D4A843]/30">{a}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Timeline Summary */}
                    {result.timeline_summary && (
                      <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#D4A843]" /> Timeline
                        </h3>
                        <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{result.timeline_summary}</p>
                      </div>
                    )}

                    {/* False Positive Assessment */}
                    {result.false_positive_assessment && (
                      <div className="bg-white border border-gray-200 rounded-lg p-5">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2 flex items-center gap-2">
                          <ShieldAlert className="w-4 h-4 text-gray-400" /> False Positive Assessment
                        </h3>
                        <p className="text-xs text-gray-600 leading-relaxed">{result.false_positive_assessment}</p>
                      </div>
                    )}
                  </>
                );
              })()}

              {!corrResult && correlations.length === 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                  <Layers className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No correlation analyses yet. Run one to detect cross-domain attack patterns.</p>
                </div>
              )}
            </>
          )}

          {/* ═══════════ AUTO-SCAN TAB ═══════════ */}
          {tab === 'Auto-Scan' && (
            <>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="autoscan-panel">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Scan className="w-4 h-4 text-[#D4A843]" /> Automated SCADA Scanning</h3>
                    <p className="text-[10px] text-gray-400 mt-1">Background anomaly detection + predictive maintenance with Slack/SMS alerts for critical findings.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button onClick={triggerManualScan} disabled={triggeringScan} variant="outline" className="text-xs rounded-lg" data-testid="autoscan-trigger">
                      {triggeringScan ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />} Scan Now
                    </Button>
                    <Button onClick={() => toggleAutoScan(!scanConfig?.enabled)} className={`text-xs rounded-lg ${scanConfig?.enabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#D4A843] hover:bg-[#E8C972]'} text-white`} data-testid="autoscan-toggle">
                      {scanConfig?.enabled ? 'Disable Auto-Scan' : 'Enable Auto-Scan'}
                    </Button>
                  </div>
                </div>
                {scanConfig && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className={`p-4 rounded-lg ${scanConfig.enabled ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <p className="text-[10px] text-gray-400 uppercase">Status</p>
                      <p className={`text-sm font-bold ${scanConfig.enabled ? 'text-emerald-700' : 'text-gray-500'}`}>{scanConfig.enabled ? 'Active' : 'Disabled'}</p>
                    </div>
                    <div className="bg-white border border-gray-200 p-4 rounded-lg">
                      <p className="text-[10px] text-gray-400 uppercase mb-1">Interval</p>
                      <select value={scanConfig.interval_hours || 6} onChange={e => updateScanInterval(Number(e.target.value))} className="h-7 rounded border border-gray-200 px-2 text-xs bg-white w-full" data-testid="autoscan-interval">
                        <option value={1}>Every 1h</option><option value={3}>Every 3h</option><option value={6}>Every 6h</option><option value={12}>Every 12h</option><option value={24}>Every 24h</option>
                      </select>
                    </div>
                    <div className="bg-white border border-gray-200 p-4 rounded-lg">
                      <p className="text-[10px] text-gray-400 uppercase">Slack Alerts</p>
                      <p className="text-sm font-bold text-gray-900">{scanConfig.alert_on_critical ? 'Critical + High' : 'Off'}</p>
                    </div>
                    <div className="bg-white border border-gray-200 p-4 rounded-lg">
                      <p className="text-[10px] text-gray-400 uppercase">SMS Alerts</p>
                      <p className="text-sm font-bold text-gray-900">{scanConfig.alert_on_high ? 'Enabled' : 'Disabled'}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="autoscan-history">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Scan History</h3>
                {scanHistoryData.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {scanHistoryData.map(s => (
                      <div key={s.id} className={`p-3 rounded-lg border ${s.alerts_sent ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-[10px] font-mono text-gray-400">{new Date(s.timestamp).toLocaleString()}</span>
                            {s.anomaly_result && <span className="text-[10px] text-gray-600">Anomalies: <span className="font-bold">{s.anomaly_result.anomalies_found}</span> / {s.anomaly_result.devices_scanned} devices</span>}
                            {s.predict_result && <span className="text-[10px] text-gray-600">Predictions: <span className="font-bold text-red-600">{s.predict_result.critical || 0}C</span> <span className="font-bold text-orange-600">{s.predict_result.high || 0}H</span></span>}
                          </div>
                          {s.alerts_sent && <span className="text-[10px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded">ALERTS SENT</span>}
                          {!s.alerts_sent && <span className="text-[10px] text-emerald-600">Clean</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-gray-400">No scans run yet. Enable auto-scan or trigger one manually.</p>}
              </div>
            </>
          )}

          {/* ═══════════ DIGITAL TWIN TAB ═══════════ */}
          {tab === 'Digital Twin' && (
            <>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="twin-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Target className="w-4 h-4 text-[#D4A843]" /> Digital Twin — Live vs Expected</h3>
                    <p className="text-[10px] text-gray-400 mt-1">Statistical models built from historical data. Compares live readings against expected behavior using z-score analysis.</p>
                  </div>
                  <Button onClick={buildTwins} disabled={buildingTwin} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs" data-testid="twin-build-btn">
                    {buildingTwin ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Target className="w-3.5 h-3.5 mr-1.5" />}
                    {buildingTwin ? 'Building...' : 'Build/Refresh Twins'}
                  </Button>
                </div>
              </div>
              {twinStatus.length > 0 ? (
                <div className="space-y-4" data-testid="twin-list">
                  {twinStatus.map(t => {
                    const hColors = { healthy: 'border-emerald-200 bg-emerald-50', attention: 'border-yellow-200 bg-yellow-50', warning: 'border-orange-200 bg-orange-50', critical: 'border-red-200 bg-red-50' };
                    const hBadge = { healthy: 'bg-emerald-100 text-emerald-700', attention: 'bg-yellow-100 text-yellow-700', warning: 'bg-orange-100 text-orange-700', critical: 'bg-red-100 text-red-700' };
                    return (
                      <div key={t.device_id} className={`border rounded-lg p-5 ${hColors[t.health] || 'border-gray-200 bg-white'}`} data-testid={`twin-${t.device_id}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <h4 className="text-sm font-bold text-gray-900">{t.device_name}</h4>
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${hBadge[t.health] || ''}`}>{t.health}</span>
                          <span className="text-[10px] text-gray-400">{t.device_type} | {t.location}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {t.metrics.map(m => {
                            const mColors = { normal: 'border-emerald-100', outside_normal: 'border-yellow-200', warning: 'border-orange-300', critical_divergence: 'border-red-400', no_data: 'border-gray-200' };
                            return (
                              <div key={m.metric} className={`bg-white rounded-lg p-3 border ${mColors[m.status] || 'border-gray-100'}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] text-gray-400 uppercase font-medium">{m.metric.replace('_', ' ')}</span>
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${m.status === 'normal' ? 'bg-emerald-100 text-emerald-700' : m.status === 'no_data' ? 'bg-gray-100 text-gray-500' : m.status === 'critical_divergence' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{m.status.replace('_', ' ')}</span>
                                </div>
                                <div className="flex items-baseline gap-2">
                                  <span className="text-lg font-bold text-gray-900">{m.live ?? '—'}</span>
                                  <span className="text-[10px] text-gray-400">expected: {m.expected_mean}</span>
                                </div>
                                {m.expected_range && <p className="text-[9px] text-gray-400">Normal range: [{m.expected_range[0]}, {m.expected_range[1]}]</p>}
                                {m.z_score !== undefined && m.z_score !== null && <p className="text-[9px] text-gray-400">Deviation: {m.deviation} | Z-score: {m.z_score}</p>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                  <Target className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No digital twins built yet. Click "Build/Refresh Twins" to create expected-output models.</p>
                </div>
              )}
            </>
          )}

          {/* ═══════════ DETECTION RULES TAB ═══════════ */}
          {tab === 'Detection Rules' && (
            <>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="detrule-panel">
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3"><ShieldAlert className="w-4 h-4 text-[#D4A843]" /> AI-Generated SCADA Detection Rules</h3>
                <p className="text-[10px] text-gray-400 mb-3">Describe an OT/SCADA threat in plain English and GPT generates a detection rule with MITRE ICS mappings.</p>
                <div className="flex gap-2">
                  <Input value={ruleDesc} onChange={e => setRuleDesc(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') generateRule(); }} placeholder="e.g., Detect unauthorized PLC firmware modifications..." className="flex-1 rounded-lg text-xs" disabled={generatingRule} data-testid="detrule-input" />
                  <Button onClick={generateRule} disabled={generatingRule || !ruleDesc.trim()} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs" data-testid="detrule-generate">
                    {generatingRule ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />} Generate Rule
                  </Button>
                </div>
              </div>
              <div className="space-y-3" data-testid="detrule-list">
                {detRules.length > 0 ? detRules.map(r => (
                  <div key={r.id} className={`bg-white border rounded-lg p-5 ${r.severity === 'critical' ? 'border-red-300' : r.severity === 'high' ? 'border-orange-300' : 'border-gray-200'}`} data-testid={`detrule-${r.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.severity === 'critical' ? 'bg-red-100 text-red-700' : r.severity === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>{r.severity}</span>
                        <h4 className="text-xs font-bold text-gray-900">{r.name}</h4>
                        {r.ai_generated && <span className="text-[9px] bg-[#D4A843]/5 text-[#D4A843] px-1.5 py-0.5 rounded">AI Generated</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant={r.enabled ? 'outline' : 'ghost'} className="text-[10px] h-6 rounded" onClick={async () => { await api.put(`/api/scada/ai/detection-rules/${r.id}`, { enabled: !r.enabled }); fetchDetRules(); }}>{r.enabled ? 'Enabled' : 'Disabled'}</Button>
                        <Button size="sm" variant="ghost" className="text-[10px] h-6 rounded text-red-500" onClick={async () => { await api.delete(`/api/scada/ai/detection-rules/${r.id}`); fetchDetRules(); toast.success('Deleted'); }}>Delete</Button>
                      </div>
                    </div>
                    <p className="text-[11px] text-gray-600 mb-2">{r.description}</p>
                    {r.mitre_ics?.length > 0 && <p className="text-[10px] text-gray-500 mb-1"><span className="font-medium">MITRE ICS:</span> {r.mitre_ics.join(', ')}</p>}
                    {r.rationale && <p className="text-[10px] text-gray-400">{r.rationale}</p>}
                    {r.response_actions?.length > 0 && (
                      <div className="mt-2">{r.response_actions.map((a, i) => <p key={`item-${i}`} className="text-[10px] text-[#D4A843] pl-2 border-l-2 border-[#D4A843]/30">{a}</p>)}</div>
                    )}
                  </div>
                )) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                    <ShieldAlert className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No detection rules yet. Generate one by describing an OT threat.</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ═══════════ PROTOCOL SIM TAB ═══════════ */}
          {tab === 'Protocol Sim' && (
            <>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="proto-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Gauge className="w-4 h-4 text-[#D4A843]" /> Protocol Simulation Layer</h3>
                    <p className="text-[10px] text-gray-400 mt-1">Simulate Modbus/DNP3/OPC-UA protocol traffic for demo and testing. Frames auto-ingest into SCADA pipeline.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <select value={simProtocol} onChange={e => setSimProtocol(e.target.value)} className="h-8 rounded-lg border border-gray-200 px-2 text-xs bg-white" data-testid="proto-select">
                      <option value="modbus">Modbus TCP/RTU</option>
                      <option value="dnp3">DNP3</option>
                      <option value="opcua">OPC-UA</option>
                    </select>
                    <Button onClick={runProtocolSim} disabled={simulating} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg text-xs" data-testid="proto-sim-btn">
                      {simulating ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />} Simulate
                    </Button>
                  </div>
                </div>
              </div>
              {protoResult && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-[10px] text-gray-400 uppercase">Protocol</p>
                      <p className="text-sm font-bold text-gray-900">{protoResult.protocol_info?.name}</p>
                      <p className="text-[9px] text-gray-400">Port {protoResult.protocol_info?.port}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-[10px] text-gray-400 uppercase">Frames</p>
                      <p className="text-sm font-bold text-gray-900">{protoResult.frames_generated}</p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-[10px] text-gray-400 uppercase">Readings Created</p>
                      <p className="text-sm font-bold text-emerald-700">{protoResult.readings_created}</p>
                    </div>
                    <div className={`rounded-lg p-4 ${protoResult.invalid_frames > 0 ? 'bg-red-50 border border-red-200' : 'bg-white border border-gray-200'}`}>
                      <p className="text-[10px] text-gray-400 uppercase">Invalid Frames</p>
                      <p className={`text-sm font-bold ${protoResult.invalid_frames > 0 ? 'text-red-700' : 'text-gray-900'}`}>{protoResult.invalid_frames}</p>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="proto-frames">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Protocol Frames</h3>
                    <div className="space-y-1 max-h-[350px] overflow-y-auto font-mono">
                      {protoResult.frames?.map(f => (
                        <div key={f.id} className={`text-[10px] p-2 rounded ${f.valid === false ? 'bg-red-50' : 'bg-gray-50'}`}>
                          <span className="text-gray-400">{new Date(f.timestamp).toLocaleTimeString()}</span>
                          {' '}
                          {f.protocol === 'modbus' && <span>FC:{f.function_code} ({f.function_name}) | Unit:{f.unit_id} | Reg:{f.start_register}+{f.register_count} | {f.response_time_ms}ms</span>}
                          {f.protocol === 'dnp3' && <span>Src:{f.source}→Dst:{f.destination} | {f.function_name} | Group:{f.object_group} | Pts:{f.data_points} | {f.response_time_ms}ms</span>}
                          {f.protocol === 'opcua' && <span>{f.service} | {f.node_id} | Status:{f.status_code} | Val:{f.value} | {f.response_time_ms}ms</span>}
                          {f.valid === false && <span className="text-red-600 ml-1 font-bold">INVALID</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {!protoResult && (
                <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                  <Gauge className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Select a protocol and click Simulate to generate demo traffic.</p>
                </div>
              )}
            </>
          )}

          {/* ═══════════ ASK YOUR PLANT TAB ═══════════ */}
          {tab === 'Ask Your Plant' && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Chat Interface */}
                <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg flex flex-col" style={{ height: 'calc(100vh - 220px)' }} data-testid="nl-query-panel">
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-[#D4A843]" /> SCADA Intelligence Assistant
                    </h3>
                    <p className="text-[10px] text-gray-400 mt-0.5">Ask questions about your plant in plain English. Powered by GPT with live device data.</p>
                  </div>

                  {/* Chat Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="nl-conversation">
                    {nlConversation.length === 0 && (
                      <div className="text-center py-12">
                        <Brain className="w-10 h-10 text-[#D4A843]/20 mx-auto mb-3" />
                        <p className="text-sm text-gray-400 mb-4">Ask anything about your SCADA systems</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {[
                            'Which devices are running hot?',
                            'Show me pressure trends',
                            'Are there any anomalies?',
                            'What is the status of all generators?',
                            'Which devices need maintenance soon?',
                          ].map(q => (
                            <button
                              key={q}
                              onClick={() => { setNlQuestion(q); }}
                              className="text-[11px] px-3 py-1.5 bg-[#D4A843]/5 border border-[#D4A843]/20 rounded-lg text-[#D4A843] hover:bg-[#D4A843]/5 transition-colors"
                              data-testid={`nl-suggestion-${q.slice(0, 15).replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              {q}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {nlConversation.map((msg, i) => (
                      <div key={`item-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-lg p-3 ${msg.role === 'user' ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-50 border border-gray-200 text-gray-800'}`}>
                          <p className="text-xs whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                          {msg.devices && <p className="text-[9px] mt-1.5 opacity-60">{msg.devices} devices analyzed</p>}
                        </div>
                      </div>
                    ))}
                    {nlLoading && (
                      <div className="flex justify-start">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <RefreshCw className="w-3 h-3 animate-spin text-[#D4A843]" />
                            <span className="text-xs text-gray-500">Analyzing plant data...</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Input */}
                  <div className="p-4 border-t border-gray-200">
                    <div className="flex gap-2">
                      <Input
                        value={nlQuestion}
                        onChange={e => setNlQuestion(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleNlQuery(); } }}
                        placeholder="Ask about your SCADA systems..."
                        className="flex-1 rounded-lg text-xs"
                        disabled={nlLoading}
                        data-testid="nl-query-input"
                      />
                      <Button
                        onClick={handleNlQuery}
                        disabled={nlLoading || !nlQuestion.trim()}
                        className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] rounded-lg"
                        data-testid="nl-query-send"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Query History Sidebar */}
                <div className="bg-white border border-gray-200 rounded-lg p-4" style={{ height: 'calc(100vh - 220px)', overflowY: 'auto' }} data-testid="nl-query-history">
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Recent Queries</h4>
                  {queryHistory.length > 0 ? (
                    <div className="space-y-3">
                      {queryHistory.map(q => (
                        <button
                          key={q.id}
                          onClick={() => setNlQuestion(q.question)}
                          className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <p className="text-xs font-medium text-gray-900 line-clamp-2">{q.question}</p>
                          <p className="text-[10px] text-gray-400 mt-1">{new Date(q.timestamp).toLocaleString()}</p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No queries yet. Try asking a question!</p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
